import * as _ from 'es-toolkit';
import RestClient from './RestClient';
import { Workflow } from '../../models';
import { getApiErrorMessage } from './getApiErrorMessage';

class Workflows extends RestClient {
    constructor() {
        super({
            "model": Workflow,
            "entryPoint": "workflow",
            "resource": "workflow",
            "resources": "workflows",
            "id": "id"
        });
    }

    startWorkflow(workflow_id, tkt_instance_id, context) {
        return this.POST(`/workflow/start/id/${workflow_id}/tkt_instance/${tkt_instance_id}`, { context })
            .then(response => this.parseApiResponse(response, 'Could not start workflow'))
            .then(json => {
                if (json.workflow) {
                    json.workflow = new Workflow(json.workflow);
                    return json;
                }
                throw new Error(getApiErrorMessage(json, 'Could not start workflow'));
            });
    }

    doneBrick(workflow_id, tkt_instance_id, context) {
        context = context || {};

        return this.POST(`/workflow/done_brick/id/${workflow_id}/tkt_instance/${tkt_instance_id}`, { context })
            .then(response => this.parseApiResponse(response, 'Could not continue workflow'))
            .then(json => {
                if (json.workflow) {
                    json.workflow = new Workflow(json.workflow);
                    return json;
                }
                throw new Error(getApiErrorMessage(json, 'Could not continue workflow'));
            });
    }

    getBrickResources(workflow_id, tkt_instance_id, brick_id) {
        const url = `/workflow/brick_resources/id/${workflow_id}` +
            `/tkt_instance/${tkt_instance_id}` +
            `/brick_id/${brick_id}`;

        return this.GET(url)
            .then(response => this.parseApiResponse(response, 'Could not load brick resources'))
            .then(json => {
                if (json.resources)
                    return json;
                throw new Error(getApiErrorMessage(json, 'Could not load brick resources'));
            });
    }

    getRuns(workflow_id = null, query_params = {}) {
        let url = '/workflow/runs';
        if (workflow_id)
            url += `/id/${workflow_id}`;

        const q = _.pickBy(query_params, (v) => {
            if (v === undefined || v === null)
                return false;
            if (Array.isArray(v))
                return v.length > 0;
            if (typeof v === 'string')
                return v.trim() !== '';
            return true;
        });

        return this.GET(url, q)
            .then(response => response.json())
            .then(json => {
                if (json.workflow_runs)
                    return json.workflow_runs;
                throw new Error(json.flash?.error || 'Could not load workflow runs');
            });
    }

    getRun(run_id, tkt_instance_id) {
        return this.GET('/workflow/run', { run_id, tkt_instance: tkt_instance_id })
            .then(response => response.json())
            .then(json => {
                if (json.flash?.error)
                    throw new Error(json.flash.error);
                if (json.workflow_run)
                    return json;
                throw new Error('Could not load workflow run');
            });
    }

    getTriggers(workflowId, tktInstanceId) {
        return this.GET('/workflow/triggers', { id: workflowId, tkt_instance: tktInstanceId })
            .then(response => response.json())
            .then(json => {
                if (Array.isArray(json.workflow_triggers))
                    return json.workflow_triggers;
                throw new Error(json.flash?.error || 'Could not load workflow triggers');
            });
    }

    createTrigger(workflowId, tktInstanceId, trigger) {
        return this.POST('/workflow/create_trigger', {
            id: workflowId,
            tkt_instance: tktInstanceId,
            trigger
        }).then(response => this.parseApiResponse(response, 'Could not create workflow trigger'));
    }

    updateTrigger(workflowId, tktInstanceId, triggerId, trigger) {
        return this.POST('/workflow/update_trigger', {
            id: workflowId,
            tkt_instance: tktInstanceId,
            trigger_id: triggerId,
            trigger
        }).then(response => this.parseApiResponse(response, 'Could not update workflow trigger'));
    }

    deleteTrigger(workflowId, tktInstanceId, triggerId) {
        return this.POST('/workflow/delete_trigger', {
            id: workflowId,
            tkt_instance: tktInstanceId,
            trigger_id: triggerId
        }).then(response => this.parseApiResponse(response, 'Could not delete workflow trigger'));
    }

    resumeRun(run_id, event = null, cart_id = null, resume_token = null, resume_context = null, comment = null) {
        return this.POST('/workflow/resume', {
            run_id,
            event,
            cart_id,
            resume_token,
            resume_context,
            comment
        }).then(response => this.parseApiResponse(response, 'Could not resume workflow run'))
            .then(json => {
                if (json.workflow) {
                    json.workflow = new Workflow(json.workflow);
                    return json;
                }
                throw new Error(json.flash?.error || 'Could not resume workflow run');
            });
    }

    deleteRun(run_id) {
        return this.POST('/workflow/delete_run', { run_id })
            .then(response => this.parseApiResponse(response, 'Could not delete workflow run'))
            .then(json => {
                if (json.workflow_run)
                    return json.workflow_run;
                throw new Error(json.flash?.error || 'Could not delete workflow run');
            });
    }

    parseApiResponse(response, fallbackMessage = 'Unexpected API error') {
        return response.json().then(json => {
            if (response.ok)
                return json;

            const error_message = getApiErrorMessage(json, fallbackMessage);
            throw new Error(`${error_message} (HTTP ${response.status})`);
        });
    }
}

const WorkflowsApi = new Workflows();

export default WorkflowsApi;
